/**
 * The SQS message the public intake endpoint enqueues, and the ONLY contract between
 * the façade (`POST /intake/{firmSlug}`, docs/25 §3 / S2) and the background writer
 * that stores the submission (`services/intake-writer`, S3).
 *
 * It lives here, in the package both sides already depend on, for the reason the
 * invite-alarm contract does NOT: these two are ordinary ESM packages in the same
 * workspace, so a shared module is available and a mirrored copy guarded by a text
 * test would be strictly worse.
 *
 * ## What each field is for
 *
 * `instanceId` is the load-bearing one. The FAÇADE mints it, BEFORE the send, and the
 * writer uses it as appcore's deterministic instance id (`SystemDb.createWithId`,
 * `packages/appcore-system`). SQS is at-least-once: a message whose send response was
 * lost, or whose consumer died after the write, is redelivered. With a server-minted id
 * that redelivery creates a SECOND intake row holding the same family's answers, which
 * an advisor then reads as two submissions. With this one, the second write hits
 * `INSTANCE_EXISTS` and the writer treats it as success. The id therefore has to be
 * decided by the side that is upstream of the retry, which is the producer.
 *
 * `firmWorkspaceId` and `tenantId` (**v1 only**) are carried so the writer can CHECK them
 * rather than trust them: it refuses any workspace but its own configured `FIRM_WORKSPACE_ID`
 * (docs/25 §3). They are resolved server-side from the firm registry, never from the
 * request body, so a caller cannot aim a submission at another workspace by asking.
 *
 * `submission` is the validated body with {@link INTAKE_HONEYPOT_FIELD} REMOVED. The
 * honeypot is an anti-spam control, not intake data, and it must never reach the stored
 * record where an advisor would read it as something the client typed.
 *
 * ## v2, and why the message SHRINKS (steward-app#743, docs/32)
 *
 * v2 drops `firmWorkspaceId` and `tenantId` and adds `firmRecordVersion`.
 *
 * **Why drop them.** The route that enqueues runs on `publicFn`, the unauthenticated
 * Lambda whose whole role is three IAM grants, and whose comment frames the question as
 * "what does an attacker who fully owns the unauthenticated request process get?". Once
 * firms are resolved from a published directory rather than from compiled-in env vars,
 * keeping these two fields on the message means that process must hold every firm's tenant
 * id, workspace id and advisor mailboxes — to populate fields the writer is about to
 * re-derive anyway. That spends the entire benefit of the public/private Lambda split for
 * nothing. The writer resolves them itself (#743 PR 7).
 *
 * **What replaces the check.** `addressedToUs` compared all three ids, and its own comment
 * says `tenantId` "is not part of the `/system/*` path, so IAM cannot enforce it at all —
 * which is precisely why it is worth checking here". If both sides resolve from ONE
 * directory, those comparisons become the directory agreeing with itself. `firmRecordVersion`
 * — appcore's `_v` on the firm record, stamped by the façade — is the one comparison that
 * does NOT go vacuous, because the two sides read the directory at different times:
 *   - writer's version **<** message's version → the writer is behind. Refuse and let SQS
 *     redeliver; the next attempt has a fresher directory.
 *   - writer's version **>** message's version → the record moved forward legitimately
 *     between accept and write. Write, and log a distinct constant — that is exactly what
 *     the accepted-vs-written reconcile wants to see rather than a silent success.
 * The invariant the shrink preserves is the original one: **the message chooses nothing; it
 * may only agree.**
 *
 * ## Versioning
 *
 * `v` is a discriminant and is checked on parse. A queue can hold messages written by the
 * previous deploy for minutes after a rollout — and the DLQ holds them for **14 days** —
 * so the writer has to be able to say "I do not understand this one" precisely, rather than
 * parsing a changed shape into a plausible-looking wrong record.
 *
 * That is why {@link IntakeQueueMessageAnySchema} accepts v1 AND v2: this is the one-release
 * dual read the rule above demands. The cutover is deliberately three steps in three PRs —
 * **define both shapes (#743 PR 3, this file), then teach the writer to read both (PR 7),
 * then flip the façade to emit v2 (PR 7)** — because a producer that emits a shape no
 * deployed consumer understands dead-letters real families' submissions.
 *
 * ⚠️ **{@link INTAKE_MESSAGE_VERSION} is the LATEST version defined here;
 * {@link INTAKE_MESSAGE_EMIT_VERSION} is what the façade actually emits.** They are EQUAL
 * again as of #743 PR 7 (both 2), and that is the steady state, not an invitation to collapse
 * them: the whole point of the pair is that a NEW shape can be defined and read before it is
 * emitted. The next wire change bumps `INTAKE_MESSAGE_VERSION` alone, ships the reader, and
 * only then moves the emit.
 */

import { z } from 'zod';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { IntakeSubmissionShape, INTAKE_HONEYPOT_FIELD } from './intakeSchema.js';

/** The latest wire version defined in this file. */
export const INTAKE_MESSAGE_VERSION = 2;

/**
 * The version the façade EMITS. Flipped to 2 by #743 PR 7, in the SAME change that taught
 * the writer to read both shapes — never before it, because a producer emitting a shape no
 * deployed consumer understands dead-letters real families' submissions.
 *
 * It stays a separate constant from {@link INTAKE_MESSAGE_VERSION} while the v1 schema is
 * still read: the two are equal today, and the day a v3 is DEFINED they must be able to
 * differ again without anyone re-deriving this argument. #743 PR 10 deletes the v1 schema
 * and this constant together, once the 14-day DLQ window has drained.
 */
export const INTAKE_MESSAGE_EMIT_VERSION = 2;

/**
 * Crockford base32 ULID, the shape appcore ids actually have. Pinned here rather than
 * left as `z.string()` so a malformed id fails at the queue boundary instead of at
 * appcore's create, where the failure costs a redelivery cycle to discover.
 *
 * EXPORTED since steward-app#1012 so `intakeDraft.ts` binds its `draftId` to the same
 * regex object rather than to a fourth copy of it — the rule `firmId` below states for
 * `ADVISOR_FIRM_SLUG_PATTERN`, applied to the other id on the wire.
 */
export const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * The submission as it travels: the validated body minus the honeypot.
 *
 * Derived with `.omit()` rather than re-declared, so the two can never disagree about
 * what an intake contains. `strictObject` survives the omit, so an unrecognized key is
 * still a rejection on the consumer side too — a message is not more trusted for having
 * been on our own queue.
 *
 * Derived from {@link IntakeSubmissionShape}, NOT the `.refine()`d `IntakeSubmissionSchema`
 * — zod 4.4.3 refuses `.omit()` on a schema carrying a refinement, and re-checking the
 * whole-submission-not-empty rule here would be redundant anyway: the façade already
 * parsed the full body with `IntakeSubmissionSchema` before this shape ever sees it, and
 * a honeypot-triggered submission is dropped before enqueue, never reaching this schema.
 */
export const IntakeQueueSubmissionSchema = IntakeSubmissionShape.omit({
  [INTAKE_HONEYPOT_FIELD]: true,
});

/** Fields carried unchanged by every version. */
const commonFields = {
  /** Deterministic appcore instance id, minted by the producer. See the note above. */
  instanceId: z.string().regex(ULID_PATTERN),
  /**
   * Slug of the firm this submission was addressed to (e.g. `ripl`).
   *
   * The bound is `ADVISOR_FIRM_SLUG_PATTERN` itself — IMPORTED, not a fourth copy of the same
   * regex. Both files are ordinary ESM modules in one package, so there is no import barrier
   * here (unlike the CDK mirror, which has one and pays for it with a text-agreement test), and
   * "the same charset, by review" is exactly the arrangement steward-app#666 records the cost
   * of. Not the old `min(1).max(64)`: the writer LOGS this field, and it used to justify that on
   * the grounds that it was "the CANONICAL slug from the compiled registry". Once the slug
   * can come from a published record that argument is gone (docs/32 §7 checklist 10), so the
   * queue boundary re-establishes it — a slug that parses cannot carry a newline, a `%`, or
   * 200 characters. This is the second of the three checks that item asks for; the resolver
   * is the first and the writer's own re-validation before the id reaches a session policy
   * is the third.
   */
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  /** When the façade accepted the submission (server clock, ISO 8601). */
  submittedAt: z.iso.datetime(),
  submission: IntakeQueueSubmissionSchema,
};

/** v1: the message as it has shipped since S2. The writer reads this until #743 PR 7. */
export const IntakeQueueMessageV1Schema = z.strictObject({
  v: z.literal(1),
  ...commonFields,
  /** Firm workspace the writer must store into — and must refuse if it is not its own. */
  firmWorkspaceId: z.string().regex(ULID_PATTERN),
  /** The firm's appcore tenant. Carried for the same check-don't-trust reason. */
  tenantId: z.string().regex(ULID_PATTERN),
});

/** v2: the slug and the record version. The writer resolves the ids itself. */
export const IntakeQueueMessageV2Schema = z.strictObject({
  v: z.literal(2),
  ...commonFields,
  /**
   * appcore's `_v` on the firm record the façade resolved. Not sensitive (it is served on
   * the public firm projection), and it is the only non-vacuous agreement check left once
   * both sides read one directory. See the header.
   */
  firmRecordVersion: z.number().int().min(0),
});

/**
 * The dual-read schema: accepts v1 and v2, discriminated on `v`.
 *
 * This is what #743 PR 7's writer parses with, so that a queue (or a 14-day DLQ) holding
 * both shapes across the rollout drains cleanly. It is deliberately NOT yet what
 * {@link IntakeQueueMessageSchema} points at — see that alias.
 */
export const IntakeQueueMessageAnySchema = z.discriminatedUnion('v', [
  IntakeQueueMessageV1Schema,
  IntakeQueueMessageV2Schema,
]);

/**
 * What the writer parses with **today**: v1 AND v2.
 *
 * Repointed at {@link IntakeQueueMessageAnySchema} by #743 PR 7, in the same change that
 * taught `addressedToUs` to branch on `v` — which is what makes the widening safe. Before
 * that change a v2 message would have reached a comparison expecting `firmWorkspaceId` and
 * `tenantId`, read `undefined` for both, and the type would no longer have stopped it.
 *
 * ⚠️ The PRODUCER does not use this alias. `handlers/intake.ts` validates its own outgoing
 * message against {@link IntakeQueueMessageV2Schema} by name, because a producer checking
 * itself against a UNION would pass while emitting either shape — which is the one thing
 * that self-check exists to catch.
 */
export const IntakeQueueMessageSchema = IntakeQueueMessageAnySchema;

export type IntakeQueueMessageV1 = z.infer<typeof IntakeQueueMessageV1Schema>;
export type IntakeQueueMessageV2 = z.infer<typeof IntakeQueueMessageV2Schema>;
/** Either version. PR 7's writer widens to this. */
export type IntakeQueueMessageAny = z.infer<typeof IntakeQueueMessageAnySchema>;
export type IntakeQueueMessage = z.infer<typeof IntakeQueueMessageSchema>;
export type IntakeQueueSubmission = z.infer<typeof IntakeQueueSubmissionSchema>;
